// src/components/leitner/GraphSketchEmbed.jsx
// Phase 6: React wrapper for Plotly-based function graph rendering

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Maximize2, Grid, Eye, EyeOff } from 'lucide-react';

// Plotly is loaded via CDN in index.html or dynamically
const loadPlotly = () => {
  return new Promise((resolve, reject) => {
    if (window.Plotly) {
      resolve(window.Plotly);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://cdn.plot.ly/plotly-latest.min.js';
    script.onload = () => resolve(window.Plotly);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const GraphSketchEmbed = ({
  graphConfig,
  equation,
  features,
  width = '100%',
  height = 300,
  showControls = true,
  showEquation = true,
  showFeatureLabels = false,
  interactive = false,
  onPointClick,
  className = ''
}) => {
  const containerRef = useRef(null);
  const plotRef = useRef(null);
  const [plotlyLoaded, setPlotlyLoaded] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showFeatures, setShowFeatures] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Load Plotly
  useEffect(() => {
    loadPlotly()
      .then(() => setPlotlyLoaded(true))
      .catch(err => console.error('Failed to load Plotly:', err));
  }, []);

  // Build plot configuration
  const plotConfig = useMemo(() => {
    if (!graphConfig) return null;

    const { traces, layout } = graphConfig;
    
    // Apply visibility settings
    const visibleTraces = traces.map((trace, i) => ({
      ...trace,
      visible: i === 0 || showFeatures
    }));

    // Apply grid setting
    const modifiedLayout = {
      ...layout,
      xaxis: {
        ...layout.xaxis,
        showgrid: showGrid
      },
      yaxis: {
        ...layout.yaxis,
        showgrid: showGrid
      },
      // Apply zoom
      ...(zoomLevel !== 1 && {
        xaxis: {
          ...layout.xaxis,
          range: layout.xaxis.range.map(v => v / zoomLevel)
        },
        yaxis: {
          ...layout.yaxis,
          range: layout.yaxis.range.map(v => v / zoomLevel)
        }
      })
    };

    return {
      traces: visibleTraces,
      layout: modifiedLayout,
      config: {
        displayModeBar: false,
        responsive: true,
        staticPlot: !interactive
      }
    };
  }, [graphConfig, showGrid, showFeatures, zoomLevel, interactive]);

  // Render plot
  useEffect(() => {
    if (!plotlyLoaded || !containerRef.current || !plotConfig) return;

    const Plotly = window.Plotly;
    
    Plotly.newPlot(
      containerRef.current,
      plotConfig.traces,
      plotConfig.layout,
      plotConfig.config
    ).then(plot => {
      plotRef.current = plot;
      
      if (onPointClick && interactive) {
        plot.on('plotly_click', (data) => {
          const point = data.points[0];
          onPointClick({ x: point.x, y: point.y });
        });
      }
    });

    return () => {
      if (containerRef.current) {
        Plotly.purge(containerRef.current);
      }
    };
  }, [plotlyLoaded, plotConfig, onPointClick, interactive]);

  // Control handlers
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev * 1.5, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev / 1.5, 0.25));
  }, []);

  const handleReset = useCallback(() => {
    setZoomLevel(1);
    setShowGrid(true);
    setShowFeatures(true);
  }, []);

  if (!graphConfig) {
    return (
      <div className={`bg-slate-800 rounded-lg p-4 flex items-center justify-center ${className}`} style={{ height }}>
        <p className="text-slate-400">No graph data available</p>
      </div>
    );
  }

  return (
    <div className={`bg-slate-800 rounded-lg overflow-hidden ${className}`}>
      {/* Equation Display */}
      {showEquation && equation && (
        <div className="px-4 py-2 bg-slate-700/50 border-b border-slate-600">
          <p className="text-center text-white font-mono text-lg">
            {equation}
          </p>
        </div>
      )}

      {/* Graph Container */}
      <div className="relative">
        <div 
          ref={containerRef} 
          style={{ width, height }}
          className="bg-white"
        />

        {/* Loading State */}
        {!plotlyLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
            <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Controls */}
      {showControls && (
        <div className="px-3 py-2 bg-slate-700/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomIn}
              className="p-1.5 rounded hover:bg-slate-600 text-slate-300 transition-colors"
              title="Zoom In"
            >
              <ZoomIn size={18} />
            </button>
            <button
              onClick={handleZoomOut}
              className="p-1.5 rounded hover:bg-slate-600 text-slate-300 transition-colors"
              title="Zoom Out"
            >
              <ZoomOut size={18} />
            </button>
            <button
              onClick={handleReset}
              className="p-1.5 rounded hover:bg-slate-600 text-slate-300 transition-colors"
              title="Reset View"
            >
              <RotateCcw size={18} />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGrid(prev => !prev)}
              className={`p-1.5 rounded transition-colors ${
                showGrid ? 'bg-indigo-600 text-white' : 'hover:bg-slate-600 text-slate-300'
              }`}
              title="Toggle Grid"
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setShowFeatures(prev => !prev)}
              className={`p-1.5 rounded transition-colors ${
                showFeatures ? 'bg-indigo-600 text-white' : 'hover:bg-slate-600 text-slate-300'
              }`}
              title="Toggle Features"
            >
              {showFeatures ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
          </div>
        </div>
      )}

      {/* Feature Labels */}
      {showFeatureLabels && features && (
        <div className="px-4 py-2 bg-slate-700/30 text-xs text-slate-400 grid grid-cols-2 gap-2">
          {features.vertex && (
            <div>
              <span className="text-slate-500">Vertex:</span>{' '}
              ({features.vertex.x}; {features.vertex.y})
            </div>
          )}
          {features.roots && features.roots.length > 0 && (
            <div>
              <span className="text-slate-500">Roots:</span>{' '}
              {features.roots.map(r => r.toFixed(2)).join(', ')}
            </div>
          )}
          {features.yIntercept !== null && (
            <div>
              <span className="text-slate-500">y-intercept:</span>{' '}
              {features.yIntercept}
            </div>
          )}
          {features.horizontalAsymptote !== null && features.horizontalAsymptote !== undefined && (
            <div>
              <span className="text-slate-500">H. Asymptote:</span>{' '}
              y = {features.horizontalAsymptote}
            </div>
          )}
          {features.verticalAsymptotes && features.verticalAsymptotes.length > 0 && (
            <div>
              <span className="text-slate-500">V. Asymptote:</span>{' '}
              x = {features.verticalAsymptotes.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Compact version for question cards
export const GraphSketchCompact = ({
  graphConfig,
  equation,
  height = 200,
  className = ''
}) => {
  return (
    <GraphSketchEmbed
      graphConfig={graphConfig}
      equation={equation}
      height={height}
      showControls={false}
      showEquation={true}
      showFeatureLabels={false}
      interactive={false}
      className={className}
    />
  );
};

// Full interactive version for exploration
export const GraphSketchInteractive = ({
  graphConfig,
  equation,
  features,
  height = 400,
  onPointClick,
  className = ''
}) => {
  return (
    <GraphSketchEmbed
      graphConfig={graphConfig}
      equation={equation}
      features={features}
      height={height}
      showControls={true}
      showEquation={true}
      showFeatureLabels={true}
      interactive={true}
      onPointClick={onPointClick}
      className={className}
    />
  );
};

export default GraphSketchEmbed;